import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import SEOHead from '@/components/feature/SEOHead';

const milestones = [
  { year: '2018', title: '平台创立', description: '91免费视频正式上线，致力于为用户提供免费的优质视频内容。' },
  { year: '2019', title: '用户破百万', description: '月活用户突破100万，成为国内知名的免费视频平台之一。' },
  { year: '2020', title: '内容扩展', description: '视频分类扩展到十大类别，覆盖娱乐、教育、科技等多个领域。' },
  { year: '2021', title: '技术升级', description: '完成全球CDN部署，实现视频秒开和高清流畅播放体验。' },
  { year: '2022', title: '用户破500万', description: '月活用户突破500万，每日视频播放量超过千万次。' },
  { year: '2023', title: '多端优化', description: '全面优化移动端体验，支持PWA技术，随时随地畅享视频。' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value || '';
    if (honeypot.trim()) {
      setFormStatus('success');
      setFormState({ name: '', email: '', message: '' });
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9sld6d8o1vn02ekuhag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormState({ name: '', email: '', message: '' });
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 91免费视频"
        description="了解91免费视频的品牌故事、发展历程和核心价值观。海量免费高清视频平台，致力于为用户提供优质的在线视频观看体验。联系我们获取更多信息。"
        keywords="91免费视频,关于我们,品牌介绍,免费视频平台,联系我们"
        canonicalUrl={`${window.location.origin}${__BASE_PATH__}/about`}
        schemaData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于91免费视频',
          description: '了解91免费视频的品牌故事、发展历程和核心价值观。',
          url: `${window.location.origin}${__BASE_PATH__}/about`,
          isPartOf: {
            '@type': 'WebSite',
            name: '91免费视频',
            url: `${window.location.origin}${__BASE_PATH__}/`,
          },
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-14 md:pt-16">
          <div className="max-w-6xl mx-auto">
            <BreadcrumbNav items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">关于91免费视频</h1>
              <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                91免费视频成立于2018年，是一家专注于免费视频内容分发的在线平台。我们致力于为用户提供海量高清视频资源，涵盖娱乐综艺、教育学习、科技数码、生活美食等十大内容分类。无需注册、无需付费，随时随地享受优质视频体验是我们的核心使命。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '10,000+', label: '高清视频' },
                  { value: '500万+', label: '月活用户' },
                  { value: '10+', label: '内容分类' },
                  { value: '6年', label: '行业深耕' },
                ].map((s) => (
                  <div key={s.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{s.value}</div>
                    <div className="text-sm text-foreground-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div key={m.year} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1.5 mt-1.5 z-10"></div>
                      <div className={`pl-10 md:pl-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-600 mb-2">{m.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">联系我们</h2>
              <p className="text-sm text-foreground-500 text-center mb-10">如果您有任何问题或建议，欢迎通过以下表单与我们联系</p>

              <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    value={formState.name}
                    onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/60 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={formState.email}
                    onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/60 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    value={formState.message}
                    onChange={(e) => setFormState((s) => ({ ...s, message: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/60 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formState.message.length}/500</p>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-hp-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : '提交留言'}
                </button>
                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-green-50 text-green-700 text-sm">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    感谢您的留言，我们会尽快回复！
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 text-red-600 text-sm">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* Organization Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: '91免费视频',
                url: `${window.location.origin}${__BASE_PATH__}/`,
                description: '91免费视频为您提供海量高清视频资源，无需注册即可免费在线观看。',
                foundingDate: '2018',
                contactPoint: {
                  '@type': 'ContactPoint',
                  telephone: '400-888-9999',
                  email: 'contact@91freevideo.com',
                  contactType: 'customer service',
                },
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: '北京市',
                  addressRegion: '朝阳区',
                  streetAddress: '科技园路88号',
                },
              }),
            }}
          />
        </main>
        <Footer />
      </div>
    </>
  );
}